import React, { useState, useEffect, useCallback } from "react";
import { useNavigate } from "react-router-dom";
import axios from "axios";
import PortalAdminLayout from "./PortalAdminLayout";
import "./PortalDashboard.css";

const API_BASE_URL =
  process.env.REACT_APP_API_BASE_URL || "https://api.ogestyle.com";

const LeadsManagement = () => {
  const navigate = useNavigate();
  const [loading, setLoading] = useState(true);
  const [leads, setLeads] = useState([]);
  const [sourceCounts, setSourceCounts] = useState({});
  const [pagination, setPagination] = useState({ page: 1, pages: 1, total: 0 });
  const [search, setSearch] = useState("");
  const [sourceFilter, setSourceFilter] = useState("");
  const [statusFilter, setStatusFilter] = useState("");
  const [selectedLead, setSelectedLead] = useState(null);
  const [showModal, setShowModal] = useState(false);
  const [error, setError] = useState("");

  const checkPortalAdmin = useCallback(() => {
    const token = localStorage.getItem("ogestyle_token");
    if (!token) {
      navigate("/login");
      return false;
    }

    try {
      const base64Url = token.split(".")[1];
      const base64 = base64Url.replace(/-/g, "+").replace(/_/g, "/");
      const jsonPayload = decodeURIComponent(
        atob(base64)
          .split("")
          .map((c) => {
            return "%" + ("00" + c.charCodeAt(0).toString(16)).slice(-2);
          })
          .join(""),
      );

      const decoded = JSON.parse(jsonPayload);
      const hasPortalAdminAccess =
        decoded.role === "portal_admin" ||
        decoded.role === "R6" ||
        (decoded.roles &&
          (decoded.roles.includes("portal_admin") ||
            decoded.roles.includes("R6")));

      if (!hasPortalAdminAccess) {
        navigate("/portal-admin");
        return false;
      }
      return true;
    } catch (error) {
      navigate("/login");
      return false;
    }
  }, [navigate]);

  const fetchLeads = useCallback(
    async (page = 1) => {
      const token = localStorage.getItem("ogestyle_token");
      setLoading(true);
      setError("");

      try {
        const params = new URLSearchParams({ page, limit: 50 });
        if (search) params.append("search", search);
        if (sourceFilter) params.append("source", sourceFilter);
        if (statusFilter) params.append("status", statusFilter);

        const response = await axios.get(
          `${API_BASE_URL}/api/admin/leads?${params}`,
          {
            headers: { Authorization: `Bearer ${token}` },
          },
        );

        setLeads(response.data.leads);
        setPagination(response.data.pagination);
        if (response.data.sourceCounts) {
          setSourceCounts(response.data.sourceCounts);
        }
      } catch (error) {
        setError("Failed to load leads");
      } finally {
        setLoading(false);
      }
    },
    [search, sourceFilter, statusFilter],
  );

  useEffect(() => {
    if (checkPortalAdmin()) {
      fetchLeads();
    }
  }, [checkPortalAdmin, fetchLeads]);

  const handleSearch = (e) => {
    e.preventDefault();
    fetchLeads(1);
  };

  const viewLeadDetails = async (leadId) => {
    const token = localStorage.getItem("ogestyle_token");
    try {
      const response = await axios.get(
        `${API_BASE_URL}/api/admin/leads/${leadId}`,
        {
          headers: { Authorization: `Bearer ${token}` },
        },
      );
      setSelectedLead(response.data.lead);
      setShowModal(true);
    } catch (error) {
      setError("Failed to load lead details");
    }
  };

  const getSourceLabel = (sourceCode) => {
    const labels = {
      newsletter: "Newsletter",
      cart_abandonment: "Cart Abandonment",
      notify_me: "Notify Me",
      get_quote: "Get Quote",
      contact_form: "Contact Form",
      product_interest: "Product Interest",
      service_interest: "Service Interest",
      checkout_guest: "Guest Checkout",
      other: "Other",
    };
    return labels[sourceCode] || sourceCode;
  };

  const formatDate = (dateString) => {
    if (!dateString) return "Never";
    return new Date(dateString).toLocaleDateString("en-US", {
      year: "numeric",
      month: "short",
      day: "numeric",
      hour: "2-digit",
      minute: "2-digit",
    });
  };

  if (loading && leads.length === 0) {
    return (
      <PortalAdminLayout>
        <div style={{ padding: "40px", textAlign: "center" }}>
          <div className="spinner"></div>
          <p>Loading leads...</p>
        </div>
      </PortalAdminLayout>
    );
  }

  return (
    <PortalAdminLayout>
      <div
        style={{
          maxWidth: "1400px",
          margin: "0 auto",
          padding: "24px",
          fontFamily:
            '-apple-system, BlinkMacSystemFont, "Segoe UI", Roboto, sans-serif',
        }}
      >
        {/* Header */}
        <div style={{ marginBottom: "24px" }}>
          <h1
            style={{ fontSize: "28px", fontWeight: "700", margin: "0 0 8px" }}
          >
            📧 Guest Leads & Analytics
          </h1>
          <p style={{ color: "#6b7280", margin: 0 }}>
            Track visitors, newsletter signups, and conversion opportunities
          </p>
        </div>

        {/* Source Cards */}
        {sourceCounts && Object.keys(sourceCounts).length > 0 && (
          <div
            style={{
              display: "grid",
              gridTemplateColumns: "repeat(auto-fit, minmax(180px, 1fr))",
              gap: "12px",
              marginBottom: "24px",
            }}
          >
            {/* All Leads Card */}
            <div
              onClick={() => {
                setSourceFilter("");
                fetchLeads(1);
              }}
              style={{
                background: sourceFilter === "" ? "#3b82f6" : "white",
                color: sourceFilter === "" ? "white" : "#1a1a1a",
                border: `2px solid ${sourceFilter === "" ? "#3b82f6" : "#e5e7eb"}`,
                borderRadius: "12px",
                padding: "16px",
                cursor: "pointer",
                transition: "all 0.2s",
              }}
            >
              <div style={{ fontSize: "24px", marginBottom: "4px" }}>📊</div>
              <div
                style={{
                  fontSize: "24px",
                  fontWeight: "700",
                  marginBottom: "2px",
                }}
              >
                {sourceCounts.all || 0}
              </div>
              <div style={{ fontSize: "12px", opacity: 0.9 }}>All Leads</div>
            </div>

            {/* Newsletter Card */}
            <div
              onClick={() => {
                setSourceFilter("newsletter");
                fetchLeads(1);
              }}
              style={{
                background: sourceFilter === "newsletter" ? "#10b981" : "white",
                color: sourceFilter === "newsletter" ? "white" : "#1a1a1a",
                border: `2px solid ${sourceFilter === "newsletter" ? "#10b981" : "#e5e7eb"}`,
                borderRadius: "12px",
                padding: "16px",
                cursor: "pointer",
                transition: "all 0.2s",
              }}
            >
              <div style={{ fontSize: "24px", marginBottom: "4px" }}>📧</div>
              <div
                style={{
                  fontSize: "24px",
                  fontWeight: "700",
                  marginBottom: "2px",
                }}
              >
                {sourceCounts.newsletter || 0}
              </div>
              <div style={{ fontSize: "12px", opacity: 0.9 }}>Newsletter</div>
            </div>

            {/* Cart Abandonment Card */}
            <div
              onClick={() => {
                setSourceFilter("cart_abandonment");
                fetchLeads(1);
              }}
              style={{
                background:
                  sourceFilter === "cart_abandonment" ? "#f59e0b" : "white",
                color:
                  sourceFilter === "cart_abandonment" ? "white" : "#1a1a1a",
                border: `2px solid ${sourceFilter === "cart_abandonment" ? "#f59e0b" : "#e5e7eb"}`,
                borderRadius: "12px",
                padding: "16px",
                cursor: "pointer",
                transition: "all 0.2s",
              }}
            >
              <div style={{ fontSize: "24px", marginBottom: "4px" }}>🛒</div>
              <div
                style={{
                  fontSize: "24px",
                  fontWeight: "700",
                  marginBottom: "2px",
                }}
              >
                {sourceCounts.cart_abandonment || 0}
              </div>
              <div style={{ fontSize: "12px", opacity: 0.9 }}>
                Cart Abandoned
              </div>
            </div>

            {/* Guest Checkout Card */}
            <div
              onClick={() => {
                setSourceFilter("checkout_guest");
                fetchLeads(1);
              }}
              style={{
                background:
                  sourceFilter === "checkout_guest" ? "#8b5cf6" : "white",
                color: sourceFilter === "checkout_guest" ? "white" : "#1a1a1a",
                border: `2px solid ${sourceFilter === "checkout_guest" ? "#8b5cf6" : "#e5e7eb"}`,
                borderRadius: "12px",
                padding: "16px",
                cursor: "pointer",
                transition: "all 0.2s",
              }}
            >
              <div style={{ fontSize: "24px", marginBottom: "4px" }}>💳</div>
              <div
                style={{
                  fontSize: "24px",
                  fontWeight: "700",
                  marginBottom: "2px",
                }}
              >
                {sourceCounts.checkout_guest || 0}
              </div>
              <div style={{ fontSize: "12px", opacity: 0.9 }}>Guest Orders</div>
            </div>

            {/* Converted Card */}
            <div
              onClick={() => {
                setStatusFilter("");
                setSourceFilter("");
                fetchLeads(1);
              }}
              style={{
                background: "#d1fae5",
                color: "#065f46",
                border: "2px solid #10b981",
                borderRadius: "12px",
                padding: "16px",
                cursor: "pointer",
                transition: "all 0.2s",
              }}
            >
              <div style={{ fontSize: "24px", marginBottom: "4px" }}>✅</div>
              <div
                style={{
                  fontSize: "24px",
                  fontWeight: "700",
                  marginBottom: "2px",
                }}
              >
                {sourceCounts.converted || 0}
              </div>
              <div style={{ fontSize: "12px", opacity: 0.9 }}>Converted</div>
            </div>
          </div>
        )}

        {error && (
          <div
            style={{
              padding: "16px",
              borderRadius: "8px",
              marginBottom: "24px",
              background: "#fee2e2",
              border: "1px solid #ef4444",
              color: "#991b1b",
            }}
          >
            {error}
          </div>
        )}

        {/* Search and Filters */}
        <div
          style={{
            background: "white",
            border: "1px solid #e5e7eb",
            borderRadius: "12px",
            padding: "20px",
            marginBottom: "24px",
          }}
        >
          <form
            onSubmit={handleSearch}
            style={{
              display: "flex",
              flexWrap: "wrap",
              gap: "16px",
              alignItems: "flex-end",
            }}
          >
            <div style={{ flex: "1 1 300px" }}>
              <label
                style={{
                  display: "block",
                  fontWeight: "500",
                  marginBottom: "8px",
                  fontSize: "14px",
                }}
              >
                Search
              </label>
              <input
                type="text"
                value={search}
                onChange={(e) => setSearch(e.target.value)}
                placeholder="Search by name or email..."
                style={{
                  width: "100%",
                  padding: "10px 14px",
                  border: "1px solid #d1d5db",
                  borderRadius: "8px",
                  fontSize: "14px",
                }}
              />
            </div>
            <div style={{ width: "180px" }}>
              <label
                style={{
                  display: "block",
                  fontWeight: "500",
                  marginBottom: "8px",
                  fontSize: "14px",
                }}
              >
                Source
              </label>
              <select
                value={sourceFilter}
                onChange={(e) => setSourceFilter(e.target.value)}
                style={{
                  width: "100%",
                  padding: "10px 14px",
                  border: "1px solid #d1d5db",
                  borderRadius: "8px",
                  fontSize: "14px",
                }}
              >
                <option value="">All Sources</option>
                <option value="newsletter">Newsletter</option>
                <option value="cart_abandonment">Cart Abandonment</option>
                <option value="notify_me">Notify Me</option>
                <option value="get_quote">Get Quote</option>
                <option value="contact_form">Contact Form</option>
                <option value="product_interest">Product Interest</option>
                <option value="service_interest">Service Interest</option>
                <option value="checkout_guest">Guest Checkout</option>
              </select>
            </div>
            <div style={{ width: "150px" }}>
              <label
                style={{
                  display: "block",
                  fontWeight: "500",
                  marginBottom: "8px",
                  fontSize: "14px",
                }}
              >
                Status
              </label>
              <select
                value={statusFilter}
                onChange={(e) => setStatusFilter(e.target.value)}
                style={{
                  width: "100%",
                  padding: "10px 14px",
                  border: "1px solid #d1d5db",
                  borderRadius: "8px",
                  fontSize: "14px",
                }}
              >
                <option value="">All Status</option>
                <option value="active">Active</option>
                <option value="converted">Converted</option>
                <option value="bounced">Bounced</option>
                <option value="unsubscribed">Unsubscribed</option>
              </select>
            </div>
            <button
              type="submit"
              style={{
                padding: "10px 24px",
                background: "#3b82f6",
                color: "white",
                border: "none",
                borderRadius: "8px",
                cursor: "pointer",
                fontWeight: "500",
                fontSize: "14px",
              }}
            >
              🔍 Search
            </button>
          </form>
        </div>

        {/* Results Summary */}
        <div
          style={{ marginBottom: "16px", color: "#6b7280", fontSize: "14px" }}
        >
          Showing {leads.length} of {pagination.total} leads
        </div>

        {/* Leads Table */}
        <div
          style={{
            background: "white",
            border: "1px solid #e5e7eb",
            borderRadius: "12px",
            overflow: "hidden",
          }}
        >
          <div style={{ overflowX: "auto" }}>
            <table
              style={{
                width: "100%",
                borderCollapse: "collapse",
                fontSize: "14px",
              }}
            >
              <thead>
                <tr
                  style={{
                    background: "#f9fafb",
                    borderBottom: "1px solid #e5e7eb",
                  }}
                >
                  <th
                    style={{
                      textAlign: "left",
                      padding: "14px 16px",
                      fontWeight: "600",
                      color: "#374151",
                    }}
                  >
                    Contact
                  </th>
                  <th
                    style={{
                      textAlign: "left",
                      padding: "14px 16px",
                      fontWeight: "600",
                      color: "#374151",
                    }}
                  >
                    Source
                  </th>
                  <th
                    style={{
                      textAlign: "center",
                      padding: "14px 16px",
                      fontWeight: "600",
                      color: "#374151",
                    }}
                  >
                    Engagement
                  </th>
                  <th
                    style={{
                      textAlign: "center",
                      padding: "14px 16px",
                      fontWeight: "600",
                      color: "#374151",
                    }}
                  >
                    Status
                  </th>
                  <th
                    style={{
                      textAlign: "left",
                      padding: "14px 16px",
                      fontWeight: "600",
                      color: "#374151",
                    }}
                  >
                    Last Activity
                  </th>
                  <th
                    style={{
                      textAlign: "center",
                      padding: "14px 16px",
                      fontWeight: "600",
                      color: "#374151",
                    }}
                  >
                    Actions
                  </th>
                </tr>
              </thead>
              <tbody>
                {leads.map((lead) => (
                  <tr
                    key={lead._id}
                    style={{ borderBottom: "1px solid #f3f4f6" }}
                  >
                    <td style={{ padding: "14px 16px" }}>
                      <div>
                        <div style={{ fontWeight: "600", color: "#1a1a1a" }}>
                          {lead.name || "Anonymous"}
                        </div>
                        <div style={{ fontSize: "13px", color: "#6b7280" }}>
                          {lead.email}
                        </div>
                        {lead.phone && (
                          <div style={{ fontSize: "12px", color: "#9ca3af" }}>
                            {lead.phone}
                          </div>
                        )}
                      </div>
                    </td>
                    <td style={{ padding: "14px 16px" }}>
                      <span
                        style={{
                          display: "inline-block",
                          padding: "4px 8px",
                          borderRadius: "4px",
                          fontSize: "12px",
                          fontWeight: "500",
                          background: "#f3f4f6",
                          color: "#374151",
                        }}
                      >
                        {getSourceLabel(lead.source)}
                      </span>
                    </td>
                    <td
                      style={{
                        padding: "14px 16px",
                        textAlign: "center",
                        fontSize: "13px",
                      }}
                    >
                      <div>{lead.totalPageViews || 0} views</div>
                      {lead.hasOrdered && (
                        <div style={{ color: "#10b981", fontSize: "12px" }}>
                          ✅ Ordered
                        </div>
                      )}
                      {lead.hasBooked && (
                        <div style={{ color: "#3b82f6", fontSize: "12px" }}>
                          📅 Booked
                        </div>
                      )}
                    </td>
                    <td style={{ padding: "14px 16px", textAlign: "center" }}>
                      <span
                        style={{
                          display: "inline-block",
                          padding: "4px 12px",
                          borderRadius: "9999px",
                          fontSize: "12px",
                          fontWeight: "500",
                          background:
                            lead.status === "converted"
                              ? "#d1fae5"
                              : lead.status === "active"
                                ? "#dbeafe"
                                : "#fee2e2",
                          color:
                            lead.status === "converted"
                              ? "#065f46"
                              : lead.status === "active"
                                ? "#1e40af"
                                : "#991b1b",
                        }}
                      >
                        {lead.status}
                      </span>
                    </td>
                    <td style={{ padding: "14px 16px", fontSize: "13px" }}>
                      {formatDate(lead.lastActivity)}
                    </td>
                    <td
                      style={{
                        padding: "14px 16px",
                        textAlign: "center",
                      }}
                    >
                      <button
                        onClick={() => viewLeadDetails(lead._id)}
                        style={{
                          padding: "6px 12px",
                          background: "#3b82f6",
                          color: "white",
                          border: "none",
                          borderRadius: "6px",
                          cursor: "pointer",
                          fontSize: "13px",
                          fontWeight: "500",
                        }}
                      >
                        View
                      </button>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </div>

        {/* Pagination */}
        {pagination.pages > 1 && (
          <div
            style={{
              display: "flex",
              justifyContent: "center",
              gap: "8px",
              marginTop: "24px",
            }}
          >
            {[...Array(pagination.pages)].map((_, i) => (
              <button
                key={i + 1}
                onClick={() => fetchLeads(i + 1)}
                style={{
                  padding: "8px 16px",
                  background: pagination.page === i + 1 ? "#3b82f6" : "white",
                  color: pagination.page === i + 1 ? "white" : "#374151",
                  border: "1px solid #d1d5db",
                  borderRadius: "6px",
                  cursor: "pointer",
                  fontWeight: pagination.page === i + 1 ? "600" : "400",
                }}
              >
                {i + 1}
              </button>
            ))}
          </div>
        )}
      </div>

      {/* Lead Details Modal */}
      {showModal && selectedLead && (
        <div
          style={{
            position: "fixed",
            top: 0,
            left: 0,
            right: 0,
            bottom: 0,
            background: "rgba(0,0,0,0.5)",
            display: "flex",
            alignItems: "center",
            justifyContent: "center",
            zIndex: 1000,
            padding: "20px",
          }}
          onClick={() => setShowModal(false)}
        >
          <div
            style={{
              background: "white",
              borderRadius: "12px",
              padding: "32px",
              maxWidth: "600px",
              width: "100%",
              maxHeight: "80vh",
              overflow: "auto",
            }}
            onClick={(e) => e.stopPropagation()}
          >
            <h2 style={{ margin: "0 0 24px", fontSize: "24px" }}>
              Lead Details
            </h2>

            <div style={{ marginBottom: "24px" }}>
              <h3
                style={{
                  fontSize: "14px",
                  fontWeight: "600",
                  marginBottom: "8px",
                  color: "#6b7280",
                }}
              >
                Contact Information
              </h3>
              <div style={{ fontSize: "14px", color: "#1a1a1a" }}>
                <p>
                  <strong>Name:</strong> {selectedLead.name || "Anonymous"}
                </p>
                <p>
                  <strong>Email:</strong> {selectedLead.email}
                </p>
                {selectedLead.phone && (
                  <p>
                    <strong>Phone:</strong> {selectedLead.phone}
                  </p>
                )}
                <p>
                  <strong>Marketing Opt-in:</strong>{" "}
                  {selectedLead.marketingOptIn ? "Yes" : "No"}
                </p>
              </div>
            </div>

            <div style={{ marginBottom: "24px" }}>
              <h3
                style={{
                  fontSize: "14px",
                  fontWeight: "600",
                  marginBottom: "8px",
                  color: "#6b7280",
                }}
              >
                Engagement
              </h3>
              <div style={{ fontSize: "14px", color: "#1a1a1a" }}>
                <p>
                  <strong>Source:</strong> {getSourceLabel(selectedLead.source)}
                </p>
                <p>
                  <strong>Status:</strong> {selectedLead.status}
                </p>
                <p>
                  <strong>Total Page Views:</strong>{" "}
                  {selectedLead.totalPageViews || 0}
                </p>
                <p>
                  <strong>Has Ordered:</strong>{" "}
                  {selectedLead.hasOrdered ? "Yes" : "No"}
                </p>
                <p>
                  <strong>Has Booked:</strong>{" "}
                  {selectedLead.hasBooked ? "Yes" : "No"}
                </p>
                <p>
                  <strong>Converted to User:</strong>{" "}
                  {selectedLead.convertedToUser ? "Yes" : "No"}
                </p>
              </div>
            </div>

            {selectedLead.interests && selectedLead.interests.length > 0 && (
              <div style={{ marginBottom: "24px" }}>
                <h3
                  style={{
                    fontSize: "14px",
                    fontWeight: "600",
                    marginBottom: "8px",
                    color: "#6b7280",
                  }}
                >
                  Interests
                </h3>
                <div style={{ display: "flex", flexWrap: "wrap", gap: "8px" }}>
                  {selectedLead.interests.map((interest, idx) => (
                    <span
                      key={idx}
                      style={{
                        padding: "4px 12px",
                        background: "#f3f4f6",
                        borderRadius: "9999px",
                        fontSize: "12px",
                      }}
                    >
                      {interest}
                    </span>
                  ))}
                </div>
              </div>
            )}

            <div style={{ marginBottom: "24px" }}>
              <h3
                style={{
                  fontSize: "14px",
                  fontWeight: "600",
                  marginBottom: "8px",
                  color: "#6b7280",
                }}
              >
                Timeline
              </h3>
              <div style={{ fontSize: "14px", color: "#1a1a1a" }}>
                <p>
                  <strong>Created:</strong> {formatDate(selectedLead.createdAt)}
                </p>
                <p>
                  <strong>Last Activity:</strong>{" "}
                  {formatDate(selectedLead.lastActivity)}
                </p>
              </div>
            </div>

            <button
              onClick={() => setShowModal(false)}
              style={{
                padding: "10px 24px",
                background: "#3b82f6",
                color: "white",
                border: "none",
                borderRadius: "8px",
                cursor: "pointer",
                fontWeight: "500",
                width: "100%",
              }}
            >
              Close
            </button>
          </div>
        </div>
      )}
    </PortalAdminLayout>
  );
};

export default LeadsManagement;
